import { useUpdateTransaction } from '../../../hooks/useUpdateTransaction';
import { useCreateTransaction } from '../../../hooks/useCreateTransaction';
import {
  ITransaction,
  IUpdateTransaction,
  ICreateTransaction,
} from '../../../interface/transaction';
import { useTransaction } from '../../../context/TransactionContext';
import { useTransactionSubmissionError } from './useTransactionSubmissionError';
import { useVersionConflict } from '../../../hooks/useVersionConflict';
import { fetchRecordVersion } from '../../../helpers/versionConflictHelper';
import { API_URLS } from '../../../constants/apiUrl';
import { TRANSACTION_QUERY_KEYS_TO_INVALIDATE } from '../../../constants/transaction';

interface UseTransactionMutationsProps {
  transactionId: string | undefined;
  handleBack: () => void;
  checkForTimerAndRemove: () => void;
  handleTransactionisFromMailOrAppointment: (transactionRecord: ITransaction) => Promise<void>;
}

export const useTransactionMutations = ({
  transactionId,
  handleBack,
  checkForTimerAndRemove,
  handleTransactionisFromMailOrAppointment,
}: UseTransactionMutationsProps) => {
  const { transaction } = useTransaction();
  const updateTransactionMutation = useUpdateTransaction();
  const createTransactionMutation = useCreateTransaction();
  const { latestVersion, handleVersionConflict } = useVersionConflict({
    fetchLatestVersion: async () =>
      transactionId
        ? fetchRecordVersion(`${API_URLS.TRANSACTION_BY_ID}${transactionId}`)
        : undefined,
    queriesToInvalidate: TRANSACTION_QUERY_KEYS_TO_INVALIDATE,
  });
  const {
    fieldError,
    clearFieldError,
    showSubmissionError,
    isErrorModalVisible,
    errorMessage,
    closeErrorModal,
  } = useTransactionSubmissionError();

  const handleEditTransaction = (
    transactionData: IUpdateTransaction,
    params: Record<string, string>,
  ) => {
    clearFieldError();
    updateTransactionMutation.mutate(
      {
        transactionId: transactionId ?? '',
        transactionData: { ...transactionData, version: latestVersion ?? transactionData.version },
        params,
      },
      {
        onSuccess: () => {
          handleBack();
        },
        onError: (error) => {
          if (!handleVersionConflict(error)) showSubmissionError(error);
        },
      },
    );
  };

  const handleCreateTransaction = (
    data: ICreateTransaction,
    params: Record<string, string>,
    activityId?: string,
    workTimeId?: string,
  ) => {
    clearFieldError();
    createTransactionMutation.mutate(
      {
        data,
        params,
        activityId,
        workTimeId,
      },
      {
        onSuccess: (transactionRecord: ITransaction) => {
          checkForTimerAndRemove();
          if (transaction?.isFromMailOrAppointment) {
            handleTransactionisFromMailOrAppointment(transactionRecord);
          } else {
            handleBack();
          }
        },
        onError: showSubmissionError,
      },
    );
  };

  return {
    handleEditTransaction,
    handleCreateTransaction,
    transactionFieldError: fieldError,
    clearTransactionFieldError: clearFieldError,
    isTransactionError: isErrorModalVisible,
    transactionErrorMessage: errorMessage,
    closeTransactionErrorModal: closeErrorModal,
  };
};
